import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Column } from '@appkit4/react-components/table';
import { Search } from '@appkit4/react-components/search';
import './FillOnlineSettings.scss';
import { AppRoutes } from '../../../Constants';
import { useGetV1DocumentTypeByProcessTypeProcessTypeId } from '../../../api/generated/endpoint';
import { DocumentTypeResponse } from '../../../api/generated/models';
import PDFTemplateModal from '../../../components/shared/pdfTemplateModal/PdfTemplateModal';

interface DocumentType {
    documentTypeName: string;
    documentTypeId: number;
}

const FillOnlineSettings: React.FC = () => {
    const [availableDocTypes, setAvailableDocTypes] = useState<
        DocumentTypeResponse[]
    >([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const pageSize = 7;
    const navigate = useNavigate();
    const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<number | null>(
        null
    );

    const { data: documentTypesData, refetch: refetchDocTypes } =
        useGetV1DocumentTypeByProcessTypeProcessTypeId(3); // Using process type 3 for FillOnline

    // Refetch logic for document types
    const fetchDocumentTypes = async () => {
        try {
            if (refetchDocTypes) {
                const result = await refetchDocTypes();
                if (result?.data) {
                    setAvailableDocTypes(result.data);
                }
            } else if (documentTypesData) {
                setAvailableDocTypes(documentTypesData);
            }
        } catch (error) {
            console.error('Error fetching document types:', error);
        }
    };

    useEffect(() => {
        fetchDocumentTypes();
    }, []);

    const filteredDocTypes = availableDocTypes.filter((docType) =>
        (docType.documentTypeName ?? '')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
    );

    const navigateToSettings = (row: DocumentType) => {
        navigate(
            `${AppRoutes.FillOnlineDocTypeSettings}?docType=${row.documentTypeName}&docTypeId=${row.documentTypeId}&processTypeId=3`
        );
    };

    const renderCell = (row: DocumentType, field: string) => (
        <span
            role="button"
            onClick={() => navigateToSettings(row)}
            aria-label="Settings"
            style={
                field === 'documentTypeId'
                    ? { marginLeft: '50px', textAlign: 'center' }
                    : {}
            }
        >
            {row[field as keyof DocumentType]}
        </span>
    );

    const handleExpand = (row: any) => {
        setSelectedDocument(row.documentTypeId);
        setIsPdfModalVisible(true);
    };

    return (
        <div className="fill-online-settings">
            <div className="header">
                <h2 className="ap-typography-heading-m title">
                    Fill Online Document Types
                </h2>
                <Search
                    className="search-docs"
                    searchType="secondary"
                    placeholder="Search document types..."
                    value={searchTerm}
                    onChange={(value: string) => setSearchTerm(value)}
                />
            </div>
            <div className="table-container">
                {filteredDocTypes.length > 0 ? (
                    <Table
                        className="extraction-table"
                        originalData={filteredDocTypes}
                        pageSize={pageSize}
                        hasTitle
                    >
                        <Column
                            field="documentTypeName"
                            renderCell={(row) => (
                                <div className="d-flex align-items-center gap-2">
                                    <span
                                        className="Appkit4-icon icon-view-outline"
                                        role="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExpand(row);
                                        }}
                                    />
                                    <span>{row.documentTypeName}</span>
                                </div>
                            )}
                        >
                            Document type
                        </Column>
                        <Column field="documentTypeId" renderCell={renderCell}>
                            Document type id
                        </Column>
                        <Column
                            field="documentTypeName"
                            style={{ width: '50px' }}
                            renderCell={(row: DocumentType) => (
                                <span
                                    className="Appkit4-icon icon-enter-right-outline action-icon"
                                    role="button"
                                    onClick={() => navigateToSettings(row)}
                                    aria-label="Settings"
                                />
                            )}
                        />
                    </Table>
                ) : (
                    <div>No document types yet</div>
                )}
            </div>

            {selectedDocument && (
                <PDFTemplateModal
                    visible={isPdfModalVisible}
                    onClose={() => {
                        setIsPdfModalVisible(false);
                        setSelectedDocument(null);
                    }}
                    documentTypeId={selectedDocument}
                />
            )}
        </div>
    );
};

export default FillOnlineSettings;
